import { create } from 'zustand'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  confirmPasswordReset,
  type User
} from 'firebase/auth'
import { doc, setDoc, getDoc, onSnapshot, updateDoc, arrayUnion, arrayRemove, collection, query, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, db, functions } from '@/config/firebase'
import { derived } from '@/lib/storeHelpers'
import { useAnalyticsStore } from './analytics'
import { PASS_TYPE_GAMES_TOTAL } from '@/utils/passTypes'
import { cityIdReadSet, normalizeCityId } from '@/utils/cityIds'
import { isTournamentArchived } from '@/utils/tournamentRegistration'

/**
 * Port of `src/stores/auth.js`.
 *
 * Web only: the legacy store's `isNative` branches (notification preference
 * loading on profile snapshots, push unregister on logout) never ran on web
 * and are dropped.
 */

export type AuthResult = { success: boolean, error?: string, [key: string]: any }

export interface AuthState {
  user: User | null
  userProfile: any
  loading: boolean
  tournamentCaptainOf: string[]
  trainingCaptainOf: string[]

  // derived
  isAuthenticated: boolean
  isAdmin: boolean
  isSuperAdmin: boolean
  isLeagueCaptain: boolean
  hasLeagueAccess: boolean
  leagueAdminOf: string[]
  isAnyLeagueAdmin: boolean
  tournamentAdminOf: string[]
  isAnyTournamentAdmin: boolean
  isAnyTournamentCaptain: boolean
  trainingAdminOf: string[]
  isAnyTrainingAdmin: boolean
  isInboxReviewer: boolean
  adminCityIds: string[]
  isAnyTrainingCaptain: boolean

  // role checks
  isCityAdmin: (cityId?: string | null) => boolean
  isCaptainOf: (teamId?: string | null) => boolean
  isLeagueAdminOf: (leagueId?: string | null) => boolean
  isTournamentAdminOf: (tournamentId?: string | null) => boolean
  isTournamentCaptainOf: (tournamentId?: string | null) => boolean
  isTrainingAdminOf: (trainingSessionId?: string | null) => boolean
  isTrainingCaptainOf: (trainingSessionId?: string | null) => boolean

  // city data
  getCityData: (cityId?: string | null) => any
  isRegularForSchedule: (scheduleKey?: string | null, cityId?: string | null) => boolean
  getCityRegulars: (cityId?: string | null) => Record<string, any>

  // multi-pass
  getAllPasses: () => any[]
  getActivePasses: () => any[]
  getCurrentPass: () => any
  getTotalGamesRemaining: () => number
  getPassInfo: () => { passType: any, passGamesRemaining: number, passStartDate: any }
  getCityPassInfo: () => { passType: any, passGamesRemaining: number, passStartDate: any }

  // auth
  initAuth: () => Promise<User | null>
  login: (email: string, password: string) => Promise<AuthResult>
  register: (email: string, password: string, userData: any) => Promise<AuthResult>
  logout: () => Promise<AuthResult>
  sendPasswordReset: (email: string) => Promise<AuthResult>
  resetPassword: (actionCode: string, newPassword: string) => Promise<AuthResult>
  updateUserProfile: (updates: any) => Promise<AuthResult>
  updateCityData: (cityId: string, cityUpdates: any) => Promise<AuthResult>
  loadUserProfile: (uid: string) => Promise<void>
  stopProfileListener: () => void
  getIdToken: () => Promise<string | null>

  // pending payments
  getPendingPayments: () => any[]
  getPaymentsDue: () => any[]
  getPendingConfirmationPayments: () => any[]
  addPendingPayment: (paymentData: any) => Promise<AuthResult>
  updatePendingPayment: (paymentId: string, updates: any) => Promise<AuthResult>
  removePendingPayment: (paymentId: string) => Promise<AuthResult>
  removePendingPaymentByGameDate: (gameDate: string) => Promise<AuthResult>
  markPaymentAsPaid: (paymentId: string, paymentMethod?: any) => Promise<AuthResult>
  getSkatePaymentFollowUps: () => any[]
  getAwaitingSkatePaymentFollowUp: () => any
  resolveSkatePaymentFollowUp: (
    followUpId: string,
    options?: { confirmed?: boolean, paidVia?: any }
  ) => Promise<AuthResult>
}

let unsubscribeProfile: (() => void) | null = null
let unsubscribeTournamentCaptainList: (() => void) | null = null
let unsubscribeTrainingCaptainList: (() => void) | null = null
let authInitialized = false
let authInitPromise: Promise<User | null> | null = null

const deriveAuth = (s: AuthState): Partial<AuthState> => {
  const profile = s.userProfile

  // Legacy isAdmin - checks if user is admin in any city or super-admin
  const isAdmin = (() => {
    if (!profile) return false
    // Check for super admin
    if (profile.isSuperAdmin) return true
    // Check for legacy isAdmin field
    if (profile.isAdmin) return true
    // Check for city admin in any city
    if (profile.cityData) {
      return Object.values(profile.cityData).some((cd: any) => cd.isAdmin)
    }
    return false
  })()

  // League admin (public league sites: metro / southshore). Per-league scoped.
  // Stored on users/{uid} as leagueAdminOf: ["metro"] | ["southshore"] | [...]
  const leagueAdminOf: string[] = !profile
    ? []
    : Array.isArray(profile.leagueAdminOf) ? profile.leagueAdminOf : []

  // Tournament admin: users/{uid}.tournamentAdminOf: string[] of tournament ids
  const tournamentAdminOf: string[] = !profile
    ? []
    : Array.isArray(profile.tournamentAdminOf) ? profile.tournamentAdminOf : []

  const trainingAdminOf: string[] = !profile
    ? []
    : Array.isArray(profile.trainingAdminOf) ? profile.trainingAdminOf : []

  const adminCityIds: string[] = []
  if (profile?.cityData) {
    for (const [id, cd] of Object.entries<any>(profile.cityData)) {
      if (cd?.isAdmin) adminCityIds.push(id)
    }
  }

  return {
    isAuthenticated: !!s.user,
    isAdmin,
    // Super admin can manage all cities
    isSuperAdmin: profile?.isSuperAdmin || false,
    isLeagueCaptain: !profile ? false : profile.isLeagueCaptain === true,
    // Payment-league captain dashboard (/league): super admin or any captain assignment
    hasLeagueAccess: (() => {
      if (!profile) return false
      if (profile.isSuperAdmin === true) return true
      if (profile.isLeagueCaptain === true) return true
      const captainOf = profile.leagueCaptainOf
      return Array.isArray(captainOf) && captainOf.length > 0
    })(),
    leagueAdminOf,
    tournamentAdminOf,
    trainingAdminOf,
    isAnyTournamentAdmin: !profile ? false : profile.isSuperAdmin ? true : tournamentAdminOf.length > 0,
    isAnyTournamentCaptain: !profile ? false : profile.isSuperAdmin ? true : s.tournamentCaptainOf.length > 0,
    isAnyTrainingAdmin: !profile ? false : profile.isSuperAdmin ? true : trainingAdminOf.length > 0,
    adminCityIds,
    isInboxReviewer: (() => {
      if (!profile) return false
      if (profile.isSuperAdmin) return true
      if (isAdmin) return true
      if (tournamentAdminOf.length > 0) return true
      if (trainingAdminOf.length > 0) return true
      return false
    })(),
    isAnyTrainingCaptain: !profile ? false : profile.isSuperAdmin ? true : s.trainingCaptainOf.length > 0,
    isAnyLeagueAdmin: !profile ? false : profile.isSuperAdmin ? true : leagueAdminOf.length > 0
  }
}

export const useAuthStore = create<AuthState>()(
  derived<AuthState>(deriveAuth)((set, get) => {
    // Check if user is admin for a specific city
    const isCityAdmin = (cityId) => {
      const userProfile = get().userProfile
      if (!userProfile) return false
      if (userProfile.isSuperAdmin) return true
      if (!cityId) return false
      return cityIdReadSet(cityId).some((id) => userProfile.cityData?.[id]?.isAdmin)
    }

    // Check if user is captain of a specific team
    const isCaptainOf = (teamId) => {
      const userProfile = get().userProfile
      if (!userProfile) return false
      if (userProfile.isSuperAdmin) return true

      const captainTeams = userProfile.leagueCaptainOf || []
      return captainTeams.some(t => t.teamId === teamId)
    }

    const isLeagueAdminOf = (leagueId) => {
      const userProfile = get().userProfile
      if (!userProfile || !leagueId) return false
      if (userProfile.isSuperAdmin) return true
      return get().leagueAdminOf.includes(leagueId)
    }

    const isTournamentAdminOf = (tournamentId) => {
      const userProfile = get().userProfile
      if (!userProfile || !tournamentId) return false
      if (userProfile.isSuperAdmin) return true
      return get().tournamentAdminOf.includes(tournamentId)
    }

    const isTournamentCaptainOf = (tournamentId) => {
      const userProfile = get().userProfile
      if (!userProfile || !tournamentId) return false
      if (userProfile.isSuperAdmin) return true
      return get().tournamentCaptainOf.includes(tournamentId)
    }

    const isTrainingAdminOf = (trainingSessionId) => {
      const userProfile = get().userProfile
      if (!userProfile || !trainingSessionId) return false
      if (userProfile.isSuperAdmin) return true
      return get().trainingAdminOf.includes(trainingSessionId)
    }

    const isTrainingCaptainOf = (trainingSessionId) => {
      const userProfile = get().userProfile
      if (!userProfile || !trainingSessionId) return false
      if (userProfile.isSuperAdmin) return true
      return get().trainingCaptainOf.includes(trainingSessionId)
    }

    // Get user's data for a specific city
    const getCityData = (cityId) => {
      const userProfile = get().userProfile
      if (!userProfile || !cityId) return null

      for (const id of cityIdReadSet(cityId)) {
        if (userProfile.cityData?.[id]) {
          return userProfile.cityData[id]
        }
      }

      // Fall back to legacy structure (for backward compatibility during migration)
      if (cityId === 'halifax' && userProfile.regulars) {
        return {
          isAdmin: userProfile.isAdmin || false,
          regulars: userProfile.regulars,
          gamesPlayed: userProfile.gamesPlayed || 0,
          passType: userProfile.passType,
          passGamesRemaining: userProfile.passGamesRemaining || 0,
          passStartDate: userProfile.passStartDate
        }
      }

      return null
    }

    // Check if user is a regular for a specific schedule
    const isRegularForSchedule = (scheduleKey, cityId) => {
      const userProfile = get().userProfile
      if (!userProfile || !scheduleKey) return false

      for (const id of cityIdReadSet(cityId)) {
        if (userProfile.cityData?.[id]?.regulars?.[scheduleKey]) {
          return true
        }
      }

      // Fall back to legacy regulars field
      if (userProfile.regulars) {
        return userProfile.regulars[scheduleKey] || false
      }

      return false
    }

    // Get user's regulars for a specific city
    const getCityRegulars = (cityId) => {
      const userProfile = get().userProfile
      if (!userProfile || !cityId) return {}

      for (const id of cityIdReadSet(cityId)) {
        if (userProfile.cityData?.[id]?.regulars) {
          return userProfile.cityData[id].regulars
        }
      }

      // Fall back for legacy data
      if (normalizeCityId(cityId) === 'halifax' && userProfile.regulars) {
        return userProfile.regulars
      }

      return {}
    }

    // Get all passes (active and exhausted)
    const getAllPasses = () => {
      const userProfile = get().userProfile
      if (!userProfile) return []

      // Check for new passes array
      if (userProfile.passes && Array.isArray(userProfile.passes)) {
        return [...userProfile.passes].sort((a, b) =>
          (new Date(a.purchaseDate) as any) - (new Date(b.purchaseDate) as any)
        )
      }

      // Migrate legacy single pass to array format (read-only)
      if (userProfile.passType) {
        const gamesTotal = PASS_TYPE_GAMES_TOTAL[userProfile.passType] ?? 999
        return [{
          id: 'legacy_pass',
          type: userProfile.passType,
          gamesTotal: gamesTotal,
          gamesRemaining: userProfile.passGamesRemaining || 0,
          purchaseDate: userProfile.passStartDate || new Date().toISOString(),
          status: userProfile.passGamesRemaining > 0 || userProfile.passType === 'full-season'
            ? 'active' : 'exhausted',
          usageHistory: []
        }]
      }

      return []
    }

    // Check if a pass is expired
    const isPassExpired = (pass) => {
      if (!pass.expiryDate) return false // No expiry = never expires (legacy passes)
      return new Date(pass.expiryDate) < new Date()
    }

    // Get only active passes (sorted by purchase date - oldest first)
    const getActivePasses = () => {
      return getAllPasses().filter(pass =>
        pass.status === 'active' &&
        (pass.type === 'full-season' || pass.gamesRemaining > 0) &&
        !isPassExpired(pass)
      )
    }

    // Get the current pass to use (oldest active pass - FIFO)
    const getCurrentPass = () => {
      const activePasses = getActivePasses()
      return activePasses.length > 0 ? activePasses[0] : null
    }

    // Get total games remaining across all active passes
    const getTotalGamesRemaining = () => {
      const activePasses = getActivePasses()

      // If any pass is full-season, return Infinity
      if (activePasses.some(p => p.type === 'full-season')) {
        return Infinity
      }

      return activePasses.reduce((sum, pass) => sum + (pass.gamesRemaining || 0), 0)
    }

    // Get pass info (global - usable across all cities) - backward compatible
    const getPassInfo = () => {
      const userProfile = get().userProfile
      if (!userProfile) {
        return { passType: null, passGamesRemaining: 0, passStartDate: null }
      }

      // Use the current (oldest active) pass for backward compatibility
      const currentPass = getCurrentPass()
      if (currentPass) {
        return {
          passType: currentPass.type,
          passGamesRemaining: currentPass.type === 'full-season' ? 999 : currentPass.gamesRemaining,
          passStartDate: currentPass.purchaseDate
        }
      }

      // Fall back to legacy fields if no passes array
      return {
        passType: userProfile.passType || null,
        passGamesRemaining: userProfile.passGamesRemaining || 0,
        passStartDate: userProfile.passStartDate || null
      }
    }

    // Alias for backward compatibility
    const getCityPassInfo = () => getPassInfo()

    // Get ID token for Cloud Function calls
    const getIdToken = async () => {
      try {
        if (auth.currentUser) {
          return await auth.currentUser.getIdToken()
        }
        return null
      } catch (error) {
        console.error('Error getting ID token:', error)
        return null
      }
    }

    const startTournamentCaptainListener = (uid) => {
      stopTournamentCaptainListener()
      if (!uid) {
        set({ tournamentCaptainOf: [] })
        return
      }

      const q = query(
        collection(db, 'tournaments'),
        where('captainUserIds', 'array-contains', uid)
      )
      unsubscribeTournamentCaptainList = onSnapshot(
        q,
        (snapshot) => {
          // Archived tournaments drop out of captain tools along with everything else.
          set({
            tournamentCaptainOf: snapshot.docs
              .filter((d) => !isTournamentArchived(d.data()))
              .map((d) => d.id)
          })
        },
        (error) => {
          console.error('Tournament captain listener failed:', error)
          set({ tournamentCaptainOf: [] })
        }
      )
    }

    const stopTournamentCaptainListener = () => {
      if (unsubscribeTournamentCaptainList) {
        unsubscribeTournamentCaptainList()
        unsubscribeTournamentCaptainList = null
      }
      set({ tournamentCaptainOf: [] })
    }

    const startTrainingCaptainListener = (uid) => {
      stopTrainingCaptainListener()
      if (!uid) {
        set({ trainingCaptainOf: [] })
        return
      }

      const q = query(
        collection(db, 'trainingSessions'),
        where('captainUserIds', 'array-contains', uid)
      )
      unsubscribeTrainingCaptainList = onSnapshot(
        q,
        (snapshot) => {
          set({ trainingCaptainOf: snapshot.docs.map((d) => d.id) })
        },
        (error) => {
          console.error('Training captain listener failed:', error)
          set({ trainingCaptainOf: [] })
        }
      )
    }

    const stopTrainingCaptainListener = () => {
      if (unsubscribeTrainingCaptainList) {
        unsubscribeTrainingCaptainList()
        unsubscribeTrainingCaptainList = null
      }
      set({ trainingCaptainOf: [] })
    }

    const initAuth = async () => {
      // If already initialized, return the existing promise or resolve immediately
      if (authInitialized) {
        return get().user
      }

      // If initialization is in progress, return the existing promise
      if (authInitPromise) {
        return authInitPromise
      }

      // Create the initialization promise
      authInitPromise = new Promise<User | null>((resolve) => {
        // Set up timeout to prevent hanging forever
        const timeout = setTimeout(() => {
          set({ loading: false })
          authInitialized = true
          resolve(null)
        }, 10000)

        // Set up the auth state listener
        onAuthStateChanged(auth, async (firebaseUser) => {
          clearTimeout(timeout)

          if (firebaseUser) {
            set({ user: firebaseUser })
            try {
              await loadUserProfile(firebaseUser.uid)
              startTournamentCaptainListener(firebaseUser.uid)
              startTrainingCaptainListener(firebaseUser.uid)
            } catch (error) {
              console.error('Failed to load user profile:', error)
            }
          } else {
            set({ user: null, userProfile: null })
            stopTournamentCaptainListener()
            stopTrainingCaptainListener()
          }

          set({ loading: false })
          authInitialized = true
          resolve(firebaseUser)
        })
      })

      return authInitPromise
    }

    const loadUserProfile = async (uid) => {
      if (unsubscribeProfile) {
        unsubscribeProfile()
      }

      try {
        const docRef = doc(db, 'users', uid)

        // First, do an initial fetch to ensure we have the profile before continuing
        // This ensures userProfile is set before login completes
        const initialDoc = await getDoc(docRef)
        if (initialDoc.exists()) {
          set({ userProfile: { id: initialDoc.id, ...initialDoc.data() } })
          // (Native-only: notification preference loading dropped on web.)
        } else if (
          import.meta.env.PROD !== true &&
          import.meta.env.VITE_USE_FIRESTORE_EMULATOR === 'true'
        ) {
          const u = auth.currentUser
          const email = u?.email || ''
          set({
            userProfile: {
              id: uid,
              email,
              name: u?.displayName || (email ? email.split('@')[0] : 'Player'),
              position: 'Forward',
              skillLevel: 2,
              cities: {},
              cityData: {},
              passes: [],
              gameHistory: [],
              // Firestore rules require users/{uid}.isSuperAdmin; stub has no doc unless you set:
              ...(import.meta.env.VITE_EMULATOR_SUPER_ADMIN === 'true'
                ? { isSuperAdmin: true }
                : {})
            }
          })
        }

        // Then set up real-time listener for ongoing updates
        unsubscribeProfile = onSnapshot(docRef, async (doc) => {
          if (doc.exists()) {
            set({ userProfile: { id: doc.id, ...doc.data() } })
            // (Native-only: notification preference loading dropped on web.)
          }
        }, (error) => {
        })
      } catch (error) {
        throw error // Re-throw so login knows it failed
      }
    }

    const stopProfileListener = () => {
      if (unsubscribeProfile) {
        unsubscribeProfile()
        unsubscribeProfile = null
      }
    }

    const login = async (email, password) => {
      try {
        const result = await signInWithEmailAndPassword(auth, email, password)

        set({ user: result.user })
        await loadUserProfile(result.user.uid)
        startTournamentCaptainListener(result.user.uid)
        startTrainingCaptainListener(result.user.uid)

        // Track login analytics
        try {
          const analyticsStore = useAnalyticsStore.getState()
          await analyticsStore.setUserId(get().user!.uid)
          await analyticsStore.trackLogin('email')
        } catch (analyticsError) {
        }

        return { success: true }
      } catch (error) {
        const code = error?.code ? String(error.code) : ''
        const msg = (error?.message && String(error.message)) || code || 'Login failed'
        let hint = ''
        if (
          import.meta.env.DEV &&
          import.meta.env.VITE_USE_AUTH_EMULATOR === 'true' &&
          (code === 'auth/invalid-credential' ||
            code === 'auth/invalid-login-credentials' ||
            code === 'auth/user-not-found' ||
            code === 'auth/wrong-password')
        ) {
          const host =
            String(import.meta.env.VITE_EMULATOR_HOST || '').trim() ||
            (typeof window !== 'undefined' ? window.location?.hostname : '') ||
            'localhost'
          const port = import.meta.env.VITE_AUTH_EMULATOR_PORT || '9099'
          hint =
            ` — Dev: use password devlocal123456. App must talk to Auth emulator at http://${host}:${port} (npm run dev, not production). If this browser cannot reach that host, set VITE_EMULATOR_HOST in .env.emulator.local and re-run npm run dev.`
        }
        return { success: false, error: msg + hint }
      }
    }

    const register = async (email, password, userData) => {
      try {
        const trimmedName = String(userData?.name || '').trim()
        if (!trimmedName) {
          return { success: false, error: 'Full name is required' }
        }

        const result = await createUserWithEmailAndPassword(auth, email, password)

        set({ user: result.user })

        // Get the city from userData or default to halifax
        const cityId = userData.cityId || 'halifax'

        // Create profile data with city-scoped fields and multi-pass support
        const profileData: Record<string, any> = {
          email: email,
          name: trimmedName,
          position: userData.position || 'Forward',
          skillLevel: userData.skillLevel || 2,
          defaultCityId: cityId,
          isSuperAdmin: false,
          trainingAdminOf: [],
          tournamentAdminOf: [],
          // Multi-pass system (global - usable across all cities)
          passes: [],
          // Legacy pass fields for backward compatibility
          passType: null,
          passGamesRemaining: 0,
          passStartDate: null,
          // City-specific data (regulars, gamesPlayed, admin status)
          cityData: {
            [cityId]: {
              isAdmin: false,
              regulars: {},
              gamesPlayed: 0
            }
          },
          gameHistory: [],
          createdAt: new Date().toISOString()
        }

        // Legacy fields for backward compatibility
        profileData.isAdmin = false
        profileData.regulars = {}
        profileData.gamesPlayed = 0

        await setDoc(doc(db, 'users', result.user.uid), profileData)
        set({ userProfile: { id: result.user.uid, ...profileData } })
        startTournamentCaptainListener(result.user.uid)
        startTrainingCaptainListener(result.user.uid)

        // Track signup analytics
        try {
          const analyticsStore = useAnalyticsStore.getState()
          await analyticsStore.setUserId(result.user.uid)
          await analyticsStore.trackSignUp('email')
          await analyticsStore.setUserProperties({
            position: userData.position || 'Forward',
            skill_level: userData.skillLevel || 2,
            default_city: cityId
          })
        } catch (analyticsError) {
        }

        return { success: true }
      } catch (error) {
        return { success: false, error: error.message || error.code }
      }
    }

    const logout = async () => {
      try {
        stopProfileListener()
        stopTournamentCaptainListener()
        stopTrainingCaptainListener()

        // (Native-only: push notification unregister dropped on web.)

        await signOut(auth)
        set({ user: null, userProfile: null })
        return { success: true }
      } catch (error) {
        return { success: false, error: error.message || error.code }
      }
    }

    // Send password reset email.
    // Goes through requestPasswordReset so the mail comes from our own SMTP sender
    // instead of Firebase's noreply@<project>.firebaseapp.com. The function still
    // mints a Firebase oobCode, so resetPassword() below is unchanged.
    const sendPasswordReset = async (email) => {
      try {
        const fn = httpsCallable(functions, 'requestPasswordReset')
        const result: any = await fn({ email })
        // Resolves the same way for unknown addresses — no account enumeration.
        return { success: true, fromEmail: result?.data?.fromEmail || '' }
      } catch (error) {
        return { success: false, error: error.message || error.code, code: error?.code || '' }
      }
    }

    // Confirm password reset with action code from email link
    const resetPassword = async (actionCode, newPassword) => {
      try {
        await confirmPasswordReset(auth, actionCode, newPassword)
        return { success: true }
      } catch (error) {
        return { success: false, error: error.message || error.code }
      }
    }

    const updateUserProfile = async (updates) => {
      try {
        const docRef = doc(db, 'users', get().user!.uid)
        await setDoc(docRef, updates, { merge: true })
        // Apply locally immediately. Check-in/waiver gates read userProfile in-memory;
        // waiting only on onSnapshot left native clients stuck prompting for waiver again.
        const userProfile = get().userProfile
        if (userProfile && updates && typeof updates === 'object') {
          set({ userProfile: { ...userProfile, ...updates } })
        }
        return { success: true }
      } catch (error) {
        return { success: false, error: error.message }
      }
    }

    // Update city-specific data for user
    const updateCityData = async (cityId, cityUpdates) => {
      const user = get().user
      if (!user || !cityId) {
        return { success: false, error: 'Invalid user or city' }
      }

      try {
        const docRef = doc(db, 'users', user.uid)
        await setDoc(docRef, {
          cityData: {
            [cityId]: cityUpdates
          }
        }, { merge: true })
        const userProfile = get().userProfile
        if (userProfile) {
          const prevCity = userProfile.cityData?.[cityId] || {}
          set({
            userProfile: {
              ...userProfile,
              cityData: {
                ...(userProfile.cityData || {}),
                [cityId]: { ...prevCity, ...cityUpdates }
              }
            }
          })
        }
        return { success: true }
      } catch (error) {
        return { success: false, error: error.message }
      }
    }

    // =============================================
    // Pending Payments Management
    // =============================================

    // Get all pending payments
    const getPendingPayments = () => {
      const userProfile = get().userProfile
      if (!userProfile) return []
      return userProfile.pendingPayments || []
    }

    // Get pending payments that need to be paid (user is on team, not just waitlist)
    const getPaymentsDue = () => {
      return getPendingPayments().filter(p =>
        p.status === 'pending' || p.status === 'due' || p.status === 'awaiting_confirmation'
      )
    }

    // Get pending payments waiting for confirmation (user on waitlist)
    const getPendingConfirmationPayments = () => {
      return getPendingPayments().filter(p => p.status === 'pending-confirmation')
    }

    /**
     * Record money owed for a game.
     *
     * Idempotent per game. Every call previously minted a fresh id and
     * arrayUnion'd it, so nothing stopped one skate producing two debts: the
     * check-in callable treats a repeat check-in as success (it returns
     * `alreadyCheckedIn: true` rather than erroring), and the client then
     * recorded the fee again. A double-tap, or a retry after a callable
     * timed out post-commit, left the player showing the single-game fee twice.
     *
     * An unsettled payment for the same game is returned as-is instead.
     */
    const addPendingPayment = async (paymentData) => {
      const user = get().user
      if (!user) {
        return { success: false, error: 'Must be logged in' }
      }

      const SETTLED = new Set(['paid', 'confirmed', 'cancelled', 'refunded'])
      const gameId = paymentData?.gameId

      if (gameId) {
        const existing = (get().userProfile?.pendingPayments || []).find(
          (p) => p?.gameId === gameId && !SETTLED.has(p?.status)
        )
        if (existing) {
          return { success: true, paymentId: existing.id, alreadyRecorded: true }
        }
      }

      try {
        const payment = {
          id: `payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          ...paymentData,
          createdAt: new Date().toISOString()
        }

        const docRef = doc(db, 'users', user.uid)
        await updateDoc(docRef, {
          pendingPayments: arrayUnion(payment)
        })

        return { success: true, paymentId: payment.id }
      } catch (error) {
        console.error('Error adding pending payment:', error)
        return { success: false, error: error.message }
      }
    }

    // Update a pending payment status (e.g., from pending-confirmation to due)
    const updatePendingPayment = async (paymentId, updates) => {
      const { user, userProfile } = get()
      if (!user || !userProfile) {
        return { success: false, error: 'Must be logged in' }
      }

      try {
        const payments = [...(userProfile.pendingPayments || [])]
        const index = payments.findIndex(p => p.id === paymentId)

        if (index === -1) {
          return { success: false, error: 'Payment not found' }
        }

        payments[index] = { ...payments[index], ...updates, updatedAt: new Date().toISOString() }

        const docRef = doc(db, 'users', user.uid)
        await updateDoc(docRef, {
          pendingPayments: payments
        })

        return { success: true }
      } catch (error) {
        console.error('Error updating pending payment:', error)
        return { success: false, error: error.message }
      }
    }

    // Remove a pending payment (when paid or cancelled)
    const removePendingPayment = async (paymentId) => {
      const { user, userProfile } = get()
      if (!user || !userProfile) {
        return { success: false, error: 'Must be logged in' }
      }

      try {
        const payment = (userProfile.pendingPayments || []).find(p => p.id === paymentId)

        if (!payment) {
          return { success: false, error: 'Payment not found' }
        }

        const docRef = doc(db, 'users', user.uid)
        await updateDoc(docRef, {
          pendingPayments: arrayRemove(payment)
        })

        return { success: true }
      } catch (error) {
        console.error('Error removing pending payment:', error)
        return { success: false, error: error.message }
      }
    }

    // Remove pending payment by game date (for checkout)
    const removePendingPaymentByGameDate = async (gameDate) => {
      const { user, userProfile } = get()
      if (!user || !userProfile) {
        return { success: false, error: 'Must be logged in' }
      }

      try {
        const payments = userProfile.pendingPayments || []

        // Filter out payments for this game date that aren't paid
        const updatedPayments = payments.filter(p =>
          !(p.gameDate === gameDate && p.status !== 'paid')
        )

        // If no change, nothing to update
        if (updatedPayments.length === payments.length) {
          return { success: true }
        }

        const docRef = doc(db, 'users', user.uid)
        await updateDoc(docRef, {
          pendingPayments: updatedPayments
        })

        return { success: true }
      } catch (error) {
        console.error('Error removing pending payment by game date:', error)
        return { success: false, error: error.message }
      }
    }

    // Mark a pending payment as paid
    const markPaymentAsPaid = async (paymentId, paymentMethod) => {
      const { user, userProfile } = get()
      if (!user || !userProfile) {
        return { success: false, error: 'Must be logged in' }
      }

      try {
        const payments = [...(userProfile.pendingPayments || [])]
        const index = payments.findIndex(p => p.id === paymentId)

        if (index === -1) {
          return { success: false, error: 'Payment not found' }
        }

        // Update the payment status to paid
        payments[index] = {
          ...payments[index],
          status: 'paid',
          paidAt: new Date().toISOString(),
          paidVia: paymentMethod
        }

        const docRef = doc(db, 'users', user.uid)
        await updateDoc(docRef, {
          pendingPayments: payments
        })

        return { success: true }
      } catch (error) {
        console.error('Error marking payment as paid:', error)
        return { success: false, error: error.message }
      }
    }

    const getSkatePaymentFollowUps = () => {
      const userProfile = get().userProfile
      if (!userProfile) return []
      return userProfile.skatePaymentFollowUps || []
    }

    const getAwaitingSkatePaymentFollowUp = () => {
      const followUps = getSkatePaymentFollowUps().filter(
        (f) => f.status === 'awaiting_confirmation'
      )
      followUps.sort((a, b) => (a.gameDate || '').localeCompare(b.gameDate || ''))
      return followUps[0] || null
    }

    const resolveSkatePaymentFollowUp = async (
      followUpId,
      { confirmed, paidVia = null }: { confirmed?: boolean, paidVia?: any } = {}
    ) => {
      const { user, userProfile } = get()
      if (!user || !userProfile) {
        return { success: false, error: 'Must be logged in' }
      }

      try {
        const followUps = [...(userProfile.skatePaymentFollowUps || [])]
        const index = followUps.findIndex((f) => f.id === followUpId)
        if (index === -1) {
          return { success: false, error: 'Follow-up not found' }
        }

        const entry = followUps[index]
        const respondedAt = new Date().toISOString()
        followUps[index] = {
          ...entry,
          status: confirmed ? 'confirmed_paid' : 'declined_unpaid',
          respondedAt,
          paidVia: confirmed ? paidVia : null
        }

        const payments = [...(userProfile.pendingPayments || [])]
        const paymentIdx = payments.findIndex(
          (p) =>
            p.gameDate === entry.gameDate &&
            (p.status === 'awaiting_confirmation' ||
              p.status === 'pending' ||
              p.status === 'due')
        )

        if (confirmed && paymentIdx >= 0) {
          payments[paymentIdx] = {
            ...payments[paymentIdx],
            status: 'paid',
            paidAt: respondedAt,
            paidVia: paidVia || payments[paymentIdx].paymentMethod || 'confirmed'
          }
        }

        const docRef = doc(db, 'users', user.uid)
        const payload: Record<string, any> = { skatePaymentFollowUps: followUps }
        if (paymentIdx >= 0) {
          payload.pendingPayments = payments
        }
        await updateDoc(docRef, payload)

        return { success: true }
      } catch (error) {
        console.error('Error resolving skate payment follow-up:', error)
        return { success: false, error: error.message }
      }
    }

    return {
      user: null,
      userProfile: null,
      loading: true,
      tournamentCaptainOf: [],
      trainingCaptainOf: [],

      // derived (recomputed by `deriveAuth`)
      isAuthenticated: false,
      isAdmin: false,
      isSuperAdmin: false,
      isLeagueCaptain: false,
      hasLeagueAccess: false,
      leagueAdminOf: [],
      isAnyLeagueAdmin: false,
      tournamentAdminOf: [],
      isAnyTournamentAdmin: false,
      isAnyTournamentCaptain: false,
      trainingAdminOf: [],
      isAnyTrainingAdmin: false,
      isInboxReviewer: false,
      adminCityIds: [],
      isAnyTrainingCaptain: false,

      isCityAdmin,
      isCaptainOf,
      isLeagueAdminOf,
      isTournamentAdminOf,
      isTournamentCaptainOf,
      isTrainingAdminOf,
      isTrainingCaptainOf,
      getCityData,
      isRegularForSchedule,
      getCityRegulars,
      // Multi-pass functions
      getAllPasses,
      getActivePasses,
      getCurrentPass,
      getTotalGamesRemaining,
      getPassInfo,
      getCityPassInfo, // Alias for backward compatibility
      initAuth,
      login,
      register,
      logout,
      sendPasswordReset,
      resetPassword,
      updateUserProfile,
      updateCityData,
      loadUserProfile,
      stopProfileListener,
      getIdToken,
      // Pending payments functions
      getPendingPayments,
      getPaymentsDue,
      getPendingConfirmationPayments,
      addPendingPayment,
      updatePendingPayment,
      removePendingPayment,
      removePendingPaymentByGameDate,
      markPaymentAsPaid,
      getSkatePaymentFollowUps,
      getAwaitingSkatePaymentFollowUp,
      resolveSkatePaymentFollowUp
    }
  })
)
